<!--
 * @Author: Merlin
 * @Date: 2024-06-14 10:43:16
 * @LastEditors: Merlin
 * @LastEditTime: 2025-05-08 17:55:38
 * @Description: 金额输入框，以及附属的金额增减按钮 
-->
<template>
    <div class="wager-wrap">
        <div class="wager-set"
            :class="{
                'wager-set--transform': true, 
            }"
        >
            <div class="input input_lg input_animated"
                style="--1e92a94a: 51.43px"
            >
                <ClientOnly>
                    <InputBoxAmount
                        class=""
                        mode="multiple"
                        prefix=""
                        :disabled="!!0"
                        :tailLong="[0, currentPairSettings?.iDecimalPlaces || 2]" 
                        v-model="wagerValue"
                        :label="$t('waves.calc_WagerAmount')"
                        :label02="sLabel02" 
                        :min="oLimits?.min"
                        :max="oLimits?.max"
                        :tip="oTips"
                    />
                </ClientOnly>
            </div>
            <button class="wager-set__btn wager-set__btn--0"
                :disabled="!fBalanceOrZero_Z"
                @click="toAmountHalf"
            >
                /2
            </button>
            <button class="wager-set__btn wager-set__btn--1"
                :disabled="!fBalanceOrZero_Z"
                @click="toAmountDouble"
            >
                x2
            </button>
            <button
                class="wager-set__btn wager-set__btn--2"
                :disabled="!fBalanceOrZero_Z"
                @click="toAmountMax"
            >
                {{ $t('Max') }} 
            </button >
        </div>
        <!--
            ↓ 错误提示
        -->
        <div class="wager-set__error"
            v-if="!fBalanceOrZero_Z && !external && oUserStore.isLoggedIn" 
        >
            {{ $t('waves.futures.errors.insufficientBalance') }} 
        </div>
    </div>
</template>

<script setup lang="ts">
import { useUserStore } from '~/stores/user'; 
import {
    oPnlFlat,
    fFlatCoeff_Ze,
    fsubstitute_K,
} from '../ts/wavesData'; 
import {
    fnGetMaxWager01_Ae,
} from '../ts/wavesFn';
import {
    oWavesBetForm_a, // 表单 
    settingsState_f, // 提取查询到的配置对象 
    fnSettingCenter_Ft,
    chartState_r, 
} from '../ts/wavesControlCenter';
import {oAmountTips} from '@/common/js/constant'; 

const props = defineProps({
    modelValue: {
        type: [Number, String], // 投入本金的值 
    },
    external: { // ↓ 组件模式标记 💥
        type: Boolean, // true/false 分别代表：在计算器窗口中/在正式的游戏面板 
    },
    externalMulti: {
        type: Number, // 在窗口模式表现为：滑杆转换的值 ◆ 在正式的游戏面板为： 空值 
    },
    externalPair: {
        type: String,
    },
    externalType: {
        type: String,
    },
    isDataReset: { // 只看到有 false 
        type: Boolean,
    },
});

const {t: $tFn} = useNuxtApp().$i18n;
const oUserStore = useUserStore(); 
const oFnSettingCenter = fnSettingCenter_Ft();
const { multi } = toRefs(oWavesBetForm_a); 
const bHasMounted = ref(false);
const emits = defineEmits([
    'update:modelValue', // 修改本金的金额 
]);

const oLimits = computed(()=>{
    if (!bHasMounted) return; 
    return  {
        min: finalMinWagerAmount_P.value,
        max: max03Final_f.value,
    };
});

const oTips = computed(()=>{
    const showTip = [
        bHasMounted,
        oUserStore.isLoggedIn,
        fBalanceOrZero_Z.value > 0,
    ].every(Boolean);
    if (showTip){
        return  {
            max: $tFn(`waves.maxWager`),
            min: $tFn(`waves.minWager`),
        };
    }
    return { max: '', min: '', };
});

// ============= ↑ 常规变量集中声明区 ================================== 
// ============= ↓ 一些 computed() 声明 ==================================  

// ↓ 最终 wager 值（当前组件核心值）  
const wagerValue = computed({
    get(){
        return props.modelValue;
    },
    set(newValue){
        // if (props.external){
        //     newValue ||= 1; 
        // }
        // 入参的小数倍数已经由 :tailLong= 在控制了 
        emits('update:modelValue', newValue);
    },
});

// =========================================================================================== 
// ========= ❤️❤️❤️ ↓ 配置信息提取专区 ↓ ❤️❤️❤️ ========================================== 
// ============================================================================================= 

// ↓ 第 1️⃣ 步，取 pair 对应的配置对象 
const currentPairSettings = computed(()=>{
    const curPairSettings = oFnSettingCenter.currentPairSettings.value;
    // ↓ 在查询结束前将通过判断执行至此 
    if (!curPairSettings) return;
    const oCrrencySetting = oFnSettingCenter.curPairSettingsAndCurrency.value;
    console.log(`当前币配置↓\n`, deepCopy(oCrrencySetting)); 
    // ↓ 用户选定的币种对 
    const oResult = {
        ...curPairSettings, // 置于上方
        ...oCrrencySetting, // 置于下方，用于覆盖上方的值，
    };
    return oResult;
});

// ↓ 正式游戏用！
// ↓ 配置表取值： maxUserPositionAmount “最大总仓值” 
const maxUserPositionAmount_we = computed(()=>{
    let iResult = (
        // ↓ 常见值： 10_000（多数值）,  100_000(BTC_USDT) 
        currentPairSettings.value?.maxUserPositionAmount
        || 0
    );
    return iResult;
});

// ↓ 配置表取值： minUserPositionAmount  “最小总仓值” 
const minUserPositionAmount_C = computed(()=>{
    const fResult = (
        // ↓ 配置表中默认值为 1， 目前只有 1 
        currentPairSettings.value?.minUserPositionAmount
        || 0
    );
    return fResult;
});

// 配置表取值： maxWagerAmount “最大下注值” 
// 正式值：仅在游戏面板中使用
const maxWagerAmount_Me = computed(()=>{
    const fResult = (
        // ↓ 原版是 50$ 
        currentPairSettings.value?.maxWagerAmount
        || 0
    );
    return fResult;
});

// 配置表取值： minWagerAmount “最小下注值” 
const minWagerAmount_$ = computed(()=>{
    const fResult = (
        currentPairSettings.value?.minWagerAmount // 目前只有 0.01 
        || 0
    );
    return fResult;
});


// =========================================================================================== 
// ========= ↑ 配置内容专区 💘💘💘 ================================================ 
// ========= ↓ 动态取值区，按 props.external 动态取值， ======================================= 
// ============================================================================================= 

// ↓ 二选一： Pair 
const sPair = computed(()=>{
    if (props.external){
        return props.externalPair; 
    }
    // ↓ 取得当前页币种 
    return chartState_r.selectedPair;
});

// ↓ 二选一： multi 滑杆值（倍数值） 
const chosenMulti_U = computed(() => (
    props.external // props.external
    ? props.externalMulti // props.externalMulti 
    : multi.value // controlsState.multi 
));

// ↓ 二选一： maxUserPositionAmount 
const chosenMaxUserPositionAmount_Q = computed(()=>(
    props.external
    ? fsubstitute_K // 常量
    : maxUserPositionAmount_we.value
));

// ↓ 二选一： maxWagerAmount 
const chosenMaxWagerAmount_ue = computed(()=>(
    props.external
    ? fsubstitute_K // 常量
    : maxWagerAmount_Me.value 
));

// ↓ 二选一： feeType  
const chosenFeeType = computed(()=>( 
    props.external 
    ? props.externalType
    : oWavesBetForm_a.feeType 
));

// ↓ 用于输入框小标题 
const sLabel02 = computed(()=>{
    const fResult = (
        Number(wagerValue.value)
        * 1 // chosenMulti_U.value 
    );
    const {currencyName} = oUserStore.oNowCurrency;
    let iTailLong = (()=>{
        if (fResult < 1) return 5;
        if (fResult < 10) return 4;
        if (fResult < 100) return 3;
        return 2;
    })();
    const sResult = getFixed(fResult, iTailLong) + ` ${currencyName.toUpperCase()}`; 
    return sResult; 
});


// ============================================================================================== 
// ========= ↑ 动态取值，按组件的服务目标动态取值， ================================================ 
// ========= ↓ 最小下注金额专区 ================================================ 
// ============================================================================================== 

// ↓ 反推“最小”下注“本金”值 💘（最小总仓值 / 杠杆倍数）
// ↓ 本值 > 配置项.minWagerAmount 时将被采用 
// ↓ 这说明 minWagerAmount 是极限地板值，无论如何不应小于此值 
const minWagerUnderleverage_se = computed(()=>{
    const fResult = (
        chosenMulti_U.value 
        // ↓ 公式： 配置表.minUserPositionAmount / chosenMulti 
        // ↓ 如果最小总仓值是 3 / 2 倍杠杆 = 1.5元（最小下注本金） 
        ? minUserPositionAmount_C.value / Number(chosenMulti_U.value)
        : 0
    );
    return fResult; 
});

// ↓ 最终的：最小下注本金值 
// ↓ 将用于金额减半操作时的下限值 
const finalMinWagerAmount_P = computed(()=>{
    let fResult = (
        // ↓ 2选1，用大的值 
        minWagerUnderleverage_se.value > minWagerAmount_$.value 
        // ↓ 杠杆倍数反推的： 最小下注本金，满足条件下可用， 
        ? minWagerUnderleverage_se.value
        // ↓ 配置表中的： 最小下注本金，这是兜底值地板值，最小不可小于此 
        : minWagerAmount_$.value
    );
    fResult = roundUpToTwoDecimalPlaces(
        fResult,
        currentPairSettings.value?.iDecimalPlaces, 
    );
    return fResult; 
});

// ↓ 保留 x 位小数（上进） 
// ↓ 示例： 1.230001 和 1.239999 => 1.24 
// ↓ 如果通过杠杆值反推需要下注 1.23001 此时视作 1.24 
function roundUpToTwoDecimalPlaces(num, iTailLong=2) {
    const factor = Math.pow(10, iTailLong);
    return Math.ceil(num * factor) / factor;
}

// ========= ↑ 最“小” 下注金额专区 ================================================ 
// ========= ↓ 最“大” 下注金额专区  ================================================ 


// ↓ 用户余额数值 
// ↓ 注意：余额不足以下注时得到0 💥💥💥
const fBalanceOrZero_Z = computed(() => { 
    const fUserBalance = oUserStore.oNowCurrency?.balance; 
    const fBalance = (
        fUserBalance >= finalMinWagerAmount_P.value
        ? fUserBalance
        : 0
    );
    if (0 && import.meta.dev){
        console.log(
            `当前币种余额：${oUserStore.oNowCurrency?.currencyName}： `,
            oUserStore.oNowCurrency?.balance.toFixed(3),
        );
    }
    return fBalance; 
});

// ↓ 最大可用本金（用户余额 - flat 手续费后 = 上述值） 
// ↓ 这是最大值的候选项01 💥 此值过大时则让贤给其它选项02 
// ↓ 用于计算用户的投注信息 
const max01UsableBlance_X = computed(() => {
    const balance = (
        props.external 
        // ↓ 选出的： maxUserPositionAmount 在计算器模式下得到巨大值，其它模式为配置值 
        ? chosenMaxUserPositionAmount_Q.value
        // ↓ 用户真实余额，余额不足时为0 
        : fBalanceOrZero_Z.value
    );
    const flatCoeff = ( 
        chosenFeeType.value === oPnlFlat.flat 
        ? fFlatCoeff_Ze // 万分之五；常量！这是计算器模式中使用的 
        : 0
    );
    const oPrams = {
        balance,
        flatCoeff,
        multi: Number(chosenMulti_U.value),
    };
    // sj as cv as Ae() 
    const result = fnGetMaxWager01_Ae(oPrams); 
    return result; 
});

// ↓ 反推“最大”下注“本金”值  💘 （最大总仓值 / 杠杆倍数）
// ↓ 此值 < maxWagerAmount 时才被采用 
const maxWagerUnderleverage_oe = computed(()=> { 
    // ↓ 公式：配置对象.maxUserPositionAmount / 杠杆倍数 
    // ↓ 示例01： 10_000 / 2倍 = 5000 最大下注本金 
    // ↓ 示例02： 10_000 / 5倍 = 2000 
    const fResult = (
        chosenMaxUserPositionAmount_Q.value
        / Number(chosenMulti_U.value || 1)
    );
    return fResult; 
});

// ↓ 这是最大值的候选项02 💥 
// ↓ 这是下注本金的棚顶顶，最终本金不得高于此值，
// ↓ 获取 maxWagerUnderleverage_oe.value 和 ue.value 中的最小值 
const max02FinalMaxVal_re = computed(()=>{
    // ↓ 选出一个更小的值 
    const fResult = (
        chosenMaxWagerAmount_ue.value > maxWagerUnderleverage_oe.value 
        ? maxWagerUnderleverage_oe.value // 
        // ↓ 窗口模式中将取到常量 fsubstitute_K(一百万)， 其它情况取配置表.maxWagerAmount 
        : chosenMaxWagerAmount_ue.value
    );
    return fResult; 
});

// ↓ 最大值（最终）
// ↓ 点击【max】时直接调用此值赋值 💥 
const max03Final_f = computed(()=>{
    const fResult = (
        // ↓ 首选值“过大” 就让贤（不得超过棚顶值） 
        max01UsableBlance_X.value > max02FinalMaxVal_re.value 
        ? max02FinalMaxVal_re.value
        : max01UsableBlance_X.value
    );
    return (fResult);
});

// =============================================================================================
// ========== ↑ 最大下注金额专区  ================================================ 
// ========== ↑ 各种值 ==========================================================================
// ========== ↓ 一些方法 ====================================================================== 
// =============================================================================================

watch(
    ()=>{
        return [
            wagerValue.value,
            sPair.value,
            fBalanceOrZero_Z.value,
            chosenMulti_U.value,
            chosenFeeType.value,
        ];
    }, 
    async ()=>{
        const {hasLogged} = await getUserInfo(); 
        if (!hasLogged){ // 没有登录
            wagerValue.value = 0;
            return;
        }
        oWavesBetForm_a.isWagerError = !fBalanceOrZero_Z.value; 
        // console.log("监测到变化，用户余额 🎯 =>", fBalanceOrZero_Z.value); 
        // ↓ 余额不足以下注 
        if (!fBalanceOrZero_Z.value){
            wagerValue.value = 0;
            return;
        }
        const val = Number(wagerValue.value);
        if (val < finalMinWagerAmount_P.value){
            console.log("小于下限了：", val, '<', finalMinWagerAmount_P.value); 
            // console.log("配置项：", deepCopy(currentPairSettings.value));
            wagerValue.value = finalMinWagerAmount_P.value;
        }else if (val > max03Final_f.value){
            console.log("超出上限了：", val, '>', max03Final_f.value); 
            // console.log("配置项：", deepCopy(currentPairSettings.value));
            wagerValue.value = max03Final_f.value;
        }
    },
);

// ↓ 减半 
const toAmountHalf = ()=>{
    const oldValue = Number(wagerValue.value);
    const fHalfOldValue = oldValue / 2; 
    // const newValue = (
    //     finalMinWagerAmount_P.value >= fHalfOldValue
    //     ? finalMinWagerAmount_P.value
    //     : fHalfOldValue
    // );
    // ↑ 旧版 
    let newValue = fHalfOldValue;
    if (finalMinWagerAmount_P.value >= fHalfOldValue){
        newValue = finalMinWagerAmount_P.value;
        showToastIfNeeded(oAmountTips.reachedLowerLimit);
    }
    // console.log("toAmountHalf", newValue);
    wagerValue.value = newValue;
};

// ↓ 翻倍 
const toAmountDouble = ()=>{
    const oldValue = Number(wagerValue.value);
    let newValue = 0;
    if (oldValue > 0){
        // newValue = (
        //     max03Final_f.value >= oldValue * 2
        //     ? oldValue * 2
        //     : max03Final_f.value
        // );
        // ↑ 旧版 
        if (max03Final_f.value >= oldValue * 2){
            newValue = oldValue * 2;
        }else{
            newValue = max03Final_f.value;
            let toast = (
                (oUserStore.oNowCurrency?.balance || 0) > max03Final_f.value 
                ? oAmountTips.reachedUpperLimit
                : oAmountTips.reachedUserBalance 
            );
            showToastIfNeeded(toast);
        }
    }else{
        newValue = finalMinWagerAmount_P.value; 
    }
    console.log("toAmountDouble", newValue);
    wagerValue.value = newValue;
};

// ↓ 封顶 
const toAmountMax = ()=>{
    // console.log("toAmountMax", );
    wagerValue.value = max03Final_f.value;
};

defineExpose({
    // amount: u, // *.unmasked 值 
    // updateInputData: n, // editUnmaskedValue 值 
    maxAllowed: max03Final_f, // 最大值 
});

onMounted(() => {
    // console.log("onMounted 🔶");
    bHasMounted.value = true; 
});


</script>





<style scoped lang="scss">

.wager-wrap .wager-set {
    display: grid;
    grid-area: wager-set;
    grid-template-areas: "wager-set-input wager-set-input wager-set-input" "wager-set-btn0 wager-set-btn1 wager-set-btn2";
    grid-gap: 4px
}

.wager-wrap .wager-set .input {
    grid-area: wager-set-input;
    min-width: 160px
}

.wager-wrap .wager-set__btn {
    align-items: center;
    background: var(--color_grey-100);
    border: 1px solid var(--color_grey-60);
    border-radius: 6px;
    color: var(--color_grey-40);
    display: flex;
    font-size: 12px;
    font-weight: 600;
    justify-content: center;
    letter-spacing: -.02em;
    line-height: 16px;
    margin-bottom: 0;
    margin-top: 0;
    padding: 4px 12px
}

.wager-wrap .wager-set__btn:active,
.wager-wrap .wager-set__btn:hover {
    background-color: var(--color_grey-80);
    color: var(--color_grey-20)
}

.wager-wrap .wager-set__btn:disabled {
    opacity: .7;
    pointer-events: none
}

.wager-wrap .wager-set__btn--0 {
    grid-area: wager-set-btn0
}

.wager-wrap .wager-set__btn--1 {
    grid-area: wager-set-btn1
}

.wager-wrap .wager-set__btn--2 {
    grid-area: wager-set-btn2
}

.wager-wrap .wager-set__btn--3 {
    grid-area: wager-set-btn3
}


.wager-wrap .wager-set__error {
    color: var(--color_red);
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    margin-bottom: 0;
    margin-top: 14px
}

@media (max-width: 639px) {
    .wager-wrap .wager-set--transform {
        grid-template-areas:"wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn0" "wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn1 ";
        grid-column-gap: 8px;
        grid-row-gap: 0
    }

    .wager-wrap .wager-set--transform .wager-set__btn--0 {
        border-bottom: none;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset
    }

    .wager-wrap .wager-set--transform .wager-set__btn--1 {
        border-top-left-radius: unset;
        border-top-right-radius: unset
    }
}

@media (max-width: 871px) {
    .opened-left-panel .wager-wrap .wager-set--transform {
        grid-template-areas:"wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn0" "wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn1 ";
        grid-column-gap: 8px;
        grid-row-gap: 0
    }

    .opened-left-panel .wager-wrap .wager-set--transform .wager-set__btn--0 {
        border-bottom: none;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset
    }

    .opened-left-panel .wager-wrap .wager-set--transform .wager-set__btn--1 {
        border-top-left-radius: unset;
        border-top-right-radius: unset
    }
}

@media (max-width: 964px) {
    .opened-right-panel .wager-wrap .wager-set--transform {
        grid-template-areas:"wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn0" "wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn1 ";
        grid-column-gap: 8px;
        grid-row-gap: 0
    }

    .opened-right-panel .wager-wrap .wager-set--transform .wager-set__btn--0 {
        border-bottom: none;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset
    }

    .opened-right-panel .wager-wrap .wager-set--transform .wager-set__btn--1 {
        border-top-left-radius: unset;
        border-top-right-radius: unset
    }
}

@media (max-width: 979px) {
    .opened-contests-panel .wager-wrap .wager-set--transform {
        grid-template-areas:"wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn0" "wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn1 ";
        grid-column-gap: 8px;
        grid-row-gap: 0
    }

    .opened-contests-panel .wager-wrap .wager-set--transform .wager-set__btn--0 {
        border-bottom: none;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset
    }

    .opened-contests-panel .wager-wrap .wager-set--transform .wager-set__btn--1 {
        border-top-left-radius: unset;
        border-top-right-radius: unset
    }
}

@media (max-width: 1196px) {
    .opened-left-panel.opened-right-panel .wager-wrap .wager-set--transform {
        grid-template-areas:"wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn0" "wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn1 ";
        grid-column-gap: 8px;
        grid-row-gap: 0
    }

    .opened-left-panel.opened-right-panel .wager-wrap .wager-set--transform .wager-set__btn--0 {
        border-bottom: none;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset
    }

    .opened-left-panel.opened-right-panel .wager-wrap .wager-set--transform .wager-set__btn--1 {
        border-top-left-radius: unset;
        border-top-right-radius: unset
    }
}

@media (max-width: 1211px) {
    .opened-left-panel.opened-contests-panel .wager-wrap .wager-set--transform {
        grid-template-areas:"wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn0" "wager-set-btn2 wager-set-input wager-set-input wager-set-input wager-set-btn1 ";
        grid-column-gap: 8px;
        grid-row-gap: 0
    }

    .opened-left-panel.opened-contests-panel .wager-wrap .wager-set--transform .wager-set__btn--0 {
        border-bottom: none;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset
    }

    .opened-left-panel.opened-contests-panel .wager-wrap .wager-set--transform .wager-set__btn--1 {
        border-top-left-radius: unset;
        border-top-right-radius: unset
    }
}

</style>


